/**
 * COMPONENT_SPEC §5.5 `StatusTimeline` — the letter from the workshop.
 * OWNER: web-frontend-engineer.
 *
 * Real stage names in the tailor's words. NO pills, chips, badges, dots,
 * progress bars, percentages or colour of any kind. Coloured status dots do not
 * exist in this system, and the client-facing `halted` status carries no alert
 * token — it is a stage like any other, with a named person against it.
 */

import { t, tDynamic } from '@pt/copy';
import type { OrderPayload } from '../lib/contracts.ts';
import { dateInWords, joinList } from '@pt/view';
import { FactLine, Statement } from './primitives.tsx';

export function stageLabel(state: string): string {
  return tDynamic(`order.stage.label.${state}`);
}

/** The stage sentence. Every branch names the actor and the date it happened. */
function stageBody(order: OrderPayload): string {
  const tailorName = order.measurementAuthorName;
  switch (order.state) {
    case 'placed':
      return t('order.stage.body.placed', { tailorName });
    case 'confirmed':
      return order.promisedWindow === null
        ? t('order.stage.body.confirmed', { confirmerName: order.confirmedBy ?? tailorName })
        : t('order.stage.body.confirmed.window', {
            confirmerName: order.confirmedBy ?? tailorName,
            windowEarliest: dateInWords(order.promisedWindow.earliest),
            windowLatest: dateInWords(order.promisedWindow.latest),
          });
    case 'cut':
      return t('order.stage.body.cut', {
        eventDate: dateInWords(order.cutAt ?? order.lastEventAt),
        actorName: order.cutBy ?? '',
      });
    case 'constructed':
      return t('order.stage.body.constructed');
    case 'fitting':
      return t('order.stage.body.fitting', { actorName: order.fittingArrangedBy ?? tailorName });
    case 'alterations':
      return t('order.stage.body.alterations', {
        alterationAreaList: joinList(order.alterationAreas ?? []),
      });
    case 'finished':
      return t('order.stage.body.finished');
    case 'delivered':
      return t('order.stage.body.delivered', {
        eventDate: dateInWords(order.deliveredAt ?? order.lastEventAt),
        reason:
          order.deliveryMethod === 'collected'
            ? t('order.delivery.collected')
            : t('order.delivery.shipped'),
      });
    case 'halted':
      return t('order.stage.body.halted', { actorName: order.haltedBy ?? tailorName });
    case 'cancelled':
      return t('order.stage.body.cancelled', {
        eventDate: dateInWords(order.cancelledAt ?? order.lastEventAt),
        cancelReason: order.cancelledReason ?? '',
      });
    default:
      return '';
  }
}

export interface StatusTimelineProps {
  readonly order: OrderPayload;
}

export function StatusTimeline({ order }: StatusTimelineProps) {
  return (
    <div>
      <h2 className="pt-cutsheet__blockTitle">{stageLabel(order.state)}</h2>
      <Statement>{stageBody(order)}</Statement>

      {/* ST-ORDER-NODATE — a SENTENCE, never a dash, never a range, never a
          hedged number. It names who will give the date and when. */}
      {order.promisedWindow === null ? (
        <Statement>{t('order.nodate.statement', { tailorName: order.measurementAuthorName })}</Statement>
      ) : null}

      {/* ST-ORDER-QUIET — silence is a designed state. */}
      {order.daysInState >= 14 ? (
        <Statement>
          {t('order.quiet.statement', { lastEventDate: dateInWords(order.lastEventAt) })}
        </Statement>
      ) : null}

      {/* ST-ORDER-NOTE — verbatim, attributed to a named person, dated. */}
      {order.clientVisibleNotes.map((note) => (
        <div key={`${note.at}-${note.authorName}`} className="pt-block">
          <p className="pt-record__notes">{note.text}</p>
          <FactLine>
            {t('order.note.attribution', {
              authorName: note.authorName,
              noteDate: dateInWords(note.at),
            })}
          </FactLine>
        </div>
      ))}

      <section className="pt-section">
        <h2 className="pt-record__groupHeading">{t('order.history.heading')}</h2>
        <ol>
          {/* `stageHistory` carries STAGES, not events, so the client's history
              reads in the tailor's words rather than in event ids. */}
          {order.stageHistory.map((entry) => (
            <li key={`${entry.state}-${entry.at}`} className="pt-timeline__entry">
              <span className="pt-timeline__date">{dateInWords(entry.at)}</span>
              <span className="pt-timeline__stage">{stageLabel(entry.state)}</span>
              <span>{entry.byName}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* ST-ORDER-ACKS — the exact words he accepted, re-readable, including
          the cloth the colour acknowledgement was bound to. */}
      {order.acknowledgements.length === 0 ? null : (
        <section className="pt-section">
          <h2 className="pt-record__groupHeading">{t('order.acks.heading')}</h2>
          {order.acknowledgements.map((ack) => (
            <div key={ack.acknowledgementId} className="pt-block">
              <p className="pt-record__notes">
                {/* [T-46]: the exact words he accepted, stored verbatim. */}
                {t('order.acks.entry', { ackText: ack.text })}
              </p>
              <FactLine>
                {ack.fabricId == null
                  ? t('order.acks.entry.detail', { acceptedDate: dateInWords(ack.acceptedAt) })
                  : t('order.acks.entry.colour', {
                      acceptedDate: dateInWords(ack.acceptedAt),
                      clothName: order.clothName,
                    })}
              </FactLine>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
